import {
  Box, Flex, Icon, Text, Tooltip,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { FieldPathByValue, FieldValues, UseFormReturn, Controller } from "react-hook-form";
import { Maybe, Schema as YupSchema } from "yup";
import { useEffect } from "react";
import SwitchField from "components/FormElements/Fields/SwitchField";
import generateSchedulingSummary from "features/Trigger/helpers";
import TimeOfDay from "./TimeOfDay";
import { TriggerSchema } from "./schema";
import { TRIGGER_TYPE_TIMING } from "../models";
import OffsetField from "./Components/OffsetField";

interface TimingTriggerFormProps<TFieldName, TSchema extends FieldValues> {
  fieldName: TFieldName;
  form: UseFormReturn<TSchema>;
  includeTimeOfDay: boolean;
  timingOnly?: boolean;
  allowWeekendsFieldName?: string;
  allowWeekendsReadOnly?: boolean;
}

export default function TimingTriggerForm<
  TSchema extends YupSchema,
  TFieldName extends FieldPathByValue<TSchema, Maybe<TriggerSchema>> = FieldPathByValue<TSchema, Maybe<TriggerSchema>>,
>({
  fieldName,
  form,
  includeTimeOfDay,
  timingOnly = false,
  allowWeekendsFieldName,
  allowWeekendsReadOnly = false,
}: TimingTriggerFormProps<TFieldName, TSchema>) {
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = form;
  const triggerWatch = watch(fieldName);
  const trigger = triggerWatch as TriggerSchema; // FIXME: Not able to get watch to appropriately type the resulting value, so casting for the moment. Should figure this out

  // Write the defaults the UI displays (On Start Date, offset 0) into the form on mount
  // so that closing the modal without any interaction still persists the shown value.
  useEffect(() => {
    if (!trigger?.data) {
      setValue(`${fieldName}.type` as any, TRIGGER_TYPE_TIMING);
      setValue(`${fieldName}.data.referenceEvent` as any, "start_date");
      setValue(`${fieldName}.data.offset` as any, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect; setValue is stable and fieldName never changes after mount
  }, []);

  let schedulingSummary = "";
  if (trigger && trigger.type === TRIGGER_TYPE_TIMING && trigger.data) schedulingSummary = generateSchedulingSummary({ field: fieldName, trigger });

  return (
    <>
      {schedulingSummary.length > 0 && <Text fontSize="s" fontStyle="italic">{`Summary: ${schedulingSummary}`}</Text>}

      {/* Use Controller to connect OffsetField to form */}
      <Controller
        name={`${fieldName}.data.offset`}
        control={control}
        render={({ field }) => (
          <OffsetField
            value={field.value ?? 0}
            onChange={(newOffset) => {
              field.onChange(newOffset);
              if (timingOnly) {
                setValue(`${fieldName}.type`, TRIGGER_TYPE_TIMING, { shouldDirty: true });
              }
            }}
            referenceEvent={trigger?.data?.referenceEvent ?? "start_date"}
            onReferenceEventChange={(event) => setValue(`${fieldName}.data.referenceEvent` as any, event, { shouldDirty: true })}
            offsetUnit={trigger?.data?.offsetUnit}
            dayOfWeek={trigger?.data?.dayOfWeek}
            onUnitChange={(unit) => setValue(`${fieldName}.data.offsetUnit` as any, unit, { shouldDirty: true })}
            onDayChange={(day) => setValue(`${fieldName}.data.dayOfWeek` as any, day, { shouldDirty: true })}
            disabled={false}
            errors={errors}
          />
        )}
      />
      {includeTimeOfDay && <TimeOfDay<TSchema> form={form} fieldName={fieldName} allowWeekendsFieldName={allowWeekendsFieldName} />}
      {!includeTimeOfDay && allowWeekendsFieldName && (
        <Box mt={3}>
          <SwitchField
            name={allowWeekendsFieldName}
            label={(
              <Flex align="center" gap={2}>
                Allow weekend scheduling
                <Tooltip
                  label={
                    "When disabled, weekend dates are moved to weekdays. "
                    + "Dates scheduled before an event move to the Friday before the weekend. "
                    + "Dates scheduled after an event move to the Monday after the weekend."
                  }
                  fontSize="sm"
                  placement="top"
                  hasArrow
                >
                  <Icon as={InfoOutlineIcon} boxSize={3.5} color="gray.500" cursor="help" />
                </Tooltip>
              </Flex>
            )}
            control={control}
            errors={errors}
            isDisabled={allowWeekendsReadOnly}
            helperText={allowWeekendsReadOnly ? "This setting is shared with the Start Date and can only be edited there." : undefined}
            formFieldProps={{ width: "auto", pt: 2 }}
          />
        </Box>
      )}
    </>
  );
}
